import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { SplitTreatments } from '@splitsoftware/splitio-react';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { NavigationTabs } from '~components/LayoutComponents/NavigationTabs';
import { FeatureFlags } from '~constants/featureFlags';
import useMaterials from '~hooks/useMaterials/useMaterials';
import { ROUTES as routes } from '~router/routePaths';
import { useStores } from '~store';
import UserStore from '~store/UserStore';
import { useAwaitableFeatureFlag } from '~utils/hooks/useFeatureFlag';

type NavItem = {
  title: string;
  link: string;
};

const conditionalNavItems = (conditionRes: boolean, items: NavItem[]) =>
  conditionRes ? items : [];

const getNavItems = (
  featureFlags: SplitIO.TreatmentsWithConfig,
  perms: ReturnType<typeof UserStore.prototype.getPermissions>,
) => {
  const timesheetTabFeatureFlagTreatment =
    featureFlags[FeatureFlags.timesheetTab].treatment;
  const chargeBasedSettlements =
    featureFlags[FeatureFlags.chargeBasedSettlements].treatment === 'on';

  const items = [
    // NavigationTabs behaves unexpectedly when the base route is not the first item
    ...conditionalNavItems(perms.canApproveDriverDays, [
      {
        title: t('page_headings.approve_work'),
        link: `${routes.approvals.driverDay}`,
      },
    ]),
    ...conditionalNavItems(chargeBasedSettlements && perms.canApproveJob, [
      {
        title: t('approvals.approve_charges.title'),
        link: `${routes.approvals.approveCharges}`,
      },
    ]),
    ...conditionalNavItems(!chargeBasedSettlements && perms.canApproveJob, [
      {
        title: t('approvals.approve_jobs.title'),
        link: `${routes.approvals.approveJobs}`,
      },
    ]),
    ...conditionalNavItems(
      perms.canApproveDriverDays || (chargeBasedSettlements && perms.canApproveJob),
      [
        {
          title: t('page_headings.payables'),
          link: `${routes.approvals.driverPay}?category=payables`,
        },
        {
          title: t('page_headings.receivables'),
          link: `${routes.approvals.driverPay}?category=receivables`,
        },
      ],
    ),
    ...conditionalNavItems(
      timesheetTabFeatureFlagTreatment === 'on' && perms.canApproveDriverDays,
      [
        {
          title: t('approvals.timesheets.title'),
          link: `${routes.approvals.timesheets}`,
        },
      ],
    ),
  ];

  return items;
};

export const Approvals = observer(function Approvals() {
  const { loadMaterialsIntoStore } = useMaterials();
  const { userStore } = useStores();
  const perms = userStore.getPermissions();
  const { isAwaitingTreatment: isAwaitingChargeBasedSettlements } =
    useAwaitableFeatureFlag({ featureFlagKey: FeatureFlags.chargeBasedSettlements });

  useEffect(() => {
    loadMaterialsIntoStore();
  }, [loadMaterialsIntoStore]);

  const theme = useTheme();
  const location = useLocation();

  const isExternalPayablesPage = location.pathname.includes(
    routes.approvals.approveJobsByJobId,
  );

  return (
    // Split Feature Flags
    <SplitTreatments
      names={[FeatureFlags.timesheetTab, FeatureFlags.chargeBasedSettlements]}
    >
      {(splitTreatment) => {
        return (
          <>
            {!isExternalPayablesPage && (
              // The full-width divider lives on this container, not on the tabs
              // Box (which hugs its content), so the line spans the page without
              // turning the empty remainder of the row into a dead-click target
              // (TRE-15646).
              <Box
                data-test-id="approvals-nav-divider"
                sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mt: 2 }}
              >
                {!isAwaitingChargeBasedSettlements && (
                  <NavigationTabs
                    navItems={getNavItems(splitTreatment.treatments, perms)}
                  />
                )}
              </Box>
            )}

            <Box sx={{ py: 4 }}>
              <Outlet />
            </Box>
          </>
        );
      }}
    </SplitTreatments>
  );
});
